import React, { useCallback, useEffect, useState, useRef } from "react";
import NumbersIcon from "@mui/icons-material/Numbers";
import BusinessIcon from "@mui/icons-material/Business";
import AppRegistrationIcon from "@mui/icons-material/AppRegistration";
import {
  fetchOrganizationRegistration,
  fetchBusinessRegistrationType,
} from "../../../Apis/Registration-api";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import HttpIcon from "@mui/icons-material/Http";
import toast from "react-hot-toast";
import { MAIN_URL } from "../../../Configurations/Urls";
import dayjs from "dayjs";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout4 from "../../DataLayouts/Layout4";
import { operatorRequiresValue } from "../../../Components/filters/operators";
import {
  applyClientToolbarFilters,
  asOptionList,
  mergeToolbarFilters,
  ToolbarFilterSelect,
  useToolbarFilterChange,
} from "../../../Components/Table/toolbarFilters";

const REGISTRATION_TOOLBAR_FILTERS = [
  {
    urlKey: "registration_type",
    field: "organization_business_registration_type_id",
  },
];

const DEFAULT_COLUMNS = [
  {
    field: "registration_applicable",
    label: "registration_applicable",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "registration_date",
    label: "registration_date",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },

  {
    field: "registration_number",
    label: "registration_number",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
];

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "organization_business_registration_id",
  sortOrder: "desc",
};

// Helper functions
const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      !key.startsWith("filter_")
    )
      return;

    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;

      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          filterValue = filterValue
            .split(",")
            .map((v) => v.trim().toLowerCase());
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }

      filters.push({ field, operator, value: filterValue });
    }
  });

  const search = searchParams.get("search");
  if (search) {
    filters.push({
      field: "registration_number",
      operator: "contains",
      value: search,
    });
  }

  return filters;
};

function OrganizationRegistrationsList() {
  const { userData } = useAuthStore();
  const org = userData?.organization;
  const [registration, setRegistration] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(DEFAULT_COLUMNS);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [registrationTypes, setRegistrationTypes] = useState([]);
  const handleToolbarFilterChange = useToolbarFilterChange(setSearchParams);
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return dayjs(dateStr).format("DD-MM-YYYY");
  };

  // Load table configuration from general-datagrids API
  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!org?.organization_id) {
        setLoadingConfig(false);
        return;
      }

      try {
        const configRes = await fetch(`${MAIN_URL}/api/general-datagrids`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (configRes.ok) {
          const configResponse = await configRes.json();
          const datagrids = configResponse.datagrids || [];
          const orgKey = `organization_business_registrations_grid_${org.organization_id}`;
          const savedConfig = datagrids.find(
            (dg) => dg.datagrid_key === orgKey
          );

          if (savedConfig) {
            const serverCfg = savedConfig.datagrid_default_configuration;
            setTableConfig(serverCfg);

            if (
              serverCfg?.columns &&
              Array.isArray(serverCfg.columns) &&
              serverCfg.columns?.length > 0
            ) {
              setConfigColumns(serverCfg.columns);
            } else {
              setConfigColumns(DEFAULT_COLUMNS);
            }
          } else {
            setConfigColumns(DEFAULT_COLUMNS);
          }
        }
      } catch (error) {
        console.error("Error loading table configuration:", error);
        setConfigColumns(DEFAULT_COLUMNS);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadTableConfiguration();
  }, [org?.organization_id]);

  // Initialize URL parameters
  useEffect(() => {
    if (hasInitializedURL.current) return;

    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;

    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }

    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!org?.organization_id) return;
    let cancelled = false;
    fetchBusinessRegistrationType(org.organization_id)
      .then((data) => {
        if (cancelled) return;
        setRegistrationTypes(
          asOptionList(data, ["organizationbusinessregistrationtype", "data"])
        );
      })
      .catch((error) => {
        console.error("Failed to load registration types", error);
      });
    return () => {
      cancelled = true;
    };
  }, [org?.organization_id]);

  // Load registrations data
  useEffect(() => {
    if (!org?.organization_id || !hasInitializedURL.current) return;

    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();

    setLoading(true);

    const fetchData = async () => {
      try {
        const filters = mergeToolbarFilters(
          buildFiltersFromParams(searchParams),
          searchParams,
          REGISTRATION_TOOLBAR_FILTERS
        );
        const requestBody = {
          filters,
          page: Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
          sort: {
            field: searchParams.get("sortBy") || "organization_business_registration_id",
            direction: searchParams.get("sortOrder") || "desc",
          },
        };

        // Try new POST endpoint first, fallback to old GET endpoint if needed
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/registrations-filtered`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        let data;
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            data = result.data;
            
            if (result.pagination) {
              const pageSize =
                result.pagination.pageSize || result.pagination.limit || 10;
              setPaginationData({
                page:
                  result.pagination.page ||
                  Number.parseInt(searchParams.get("page"), 10) ||
                  1,
                pageSize,
                total: result.pagination.total || 0,
                totalPages: result.pagination.totalPages || 1,
                hasNext: result.pagination.hasNext || false,
                hasPrev: result.pagination.hasPrev || false,
              });
            }
          } else {
            data = [];
            setPaginationData(DEFAULT_PAGINATION);
          }
        } else if (response.status === 404) {
          // Fallback to old GET endpoint if POST endpoint doesn't exist yet
          const oldData = await fetchOrganizationRegistration(org.organization_id);
          let a = oldData?.organizationbusinessregistration?.data;
          const registrations = Array.isArray(a) ? a : [a];
          data = applyClientToolbarFilters(
            registrations.filter(Boolean),
            searchParams,
            REGISTRATION_TOOLBAR_FILTERS
          );
          setPaginationData(DEFAULT_PAGINATION);
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Transform data - preserve existing transformation logic
        const b = data.map((item) => ({
          ...item,
          id: item.organization_business_registration_id,
          registration_date: `${formatDate(item.registration_date)}`,
          registration_expiry_date: `${formatDate(item.registration_expiry_date)}`,
          registration_applicable:
            item?.registration_applicable == 0 ? "✖" : "✔",
          registration_expiry_date_applicable:
            item?.registration_expiry_date_applicable == 0 ? "✖" : "✔",
            business_registration_type_name:
            item?.business_registration_type_name ==
            null
              ? ""
              : item?.business_registration_type_name,
        }));

        setRegistration(b);
      } catch (error) {
        if (error.name === "AbortError") {
          return; // Request was cancelled, ignore
        }
        console.error("Error fetching registrations:", error);
        toast.error("Failed to load registrations");
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      fetchControllerRef.current?.abort();
    };
  }, [org?.organization_id, searchParams]);

  const deleteregistration = useCallback(
    async (id) => {
      try {
        const org_id = org.organization_id;
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org_id}/business-registration/${id}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (response.ok) {
          const result = await response.json();
          toast.success(result.message || "Registration deleted successfully");
          // Refresh data by updating search params
          const params = new URLSearchParams(searchParams);
          setSearchParams(params);
        } else {
          const errorData = await response.json();
          if (response.status === 401) {
            toast.error("Session Expired!");
            window.location.href = "/login";
          } else {
            const errorMessage =
              errorData.message ||
              errorData.errors?.[Object.keys(errorData.errors || {})[0]]?.[0] ||
              "Failed to delete Registration";
            toast.error(errorMessage);
          }
        }
      } catch (error) {
        console.error("Delete failed:", error);
        toast.error("Failed to delete Business Registration");
      }
    },
    [org?.organization_id, searchParams, setSearchParams]
  );

  const handleEdit = useCallback(
    (item) => {
      navigate(`/organization/registrations/edit/${item.id}`);
    },

    [navigate]
  );

  return (
    <>
      <Layout4
        loading={loading}
        heading={"Registrations"}
        btnName={"Add Registrations"}
        Data={registration}
        add_action={"REGISTRATION_ADD"}
        delete_action={"REGISTRATION_DELETE"}
        Icons={[
          <BusinessIcon sx={{ fontSize: 60, color: "grey.500", mb: 2 }} />,
          <NumbersIcon color="primary" />,
          <HttpIcon sx={{ color: "text.secondary" }} />,
        ]}
        tableHeaders={[
          {
            name: "Registration No.",
            value_key: "registration_number",
            textStyle: "capitalize",
          },
          { name: "Document Url", value_key: "registration_document_url" },
          { name: "Registration Date", value_key: "registration_date" },
          { name: "Expiry Date", value_key: "registration_expiry_date" },
        ]}
        messages={[
          "registrations",
          "organization registrations",
          "Add  Registrations",
          "registrations",
        ]}
        Route={"/organization/registrations"}
        setData={setRegistration}
        DeleteFunc={deleteregistration}
      />

      {!loadingConfig && (
        <TableDataGeneric
          tableName="organization_business_registrations"
          primaryKey="organization_business_registration_id"
          heading="Registrations"
          data={registration}
          sortname="registration_number"
          showActions={true}
          Route="/organization/registrations"
          DeleteFunc={deleteregistration}
          EditFunc={handleEdit}
          edit_delete_action={["REGISTRATION_DELETE", "REGISTRATION_EDIT"]}
          token={localStorage.getItem("token")}
          configss={configColumns}
          paginationData={paginationData}
          filterModule="organization_business_registrations"
          toolbarExtras={
            <ToolbarFilterSelect
              label="Registration Type"
              urlKey="registration_type"
              allLabel="All registration types"
              options={registrationTypes}
              valueKey="organization_business_registration_type_id"
              labelKey="business_registration_type_name"
              searchParams={searchParams}
              onChange={handleToolbarFilterChange}
            />
          }
          {...(tableConfig && { config: tableConfig })}
        />
      )}
    </>
  );
}

export default OrganizationRegistrationsList;
